import { getSavingsPlans, isPlanFromAPI, Plan, SavingPlanTypes } from 'api/savings-plans';
import { useConfig } from 'config/use-config';
import { LOCAL_CART_KEY } from 'constants/cart';
import { useAuth } from 'contexts/auth';
import { useCart } from 'contexts/cart';
import { toCartItem } from 'contexts/cart/utils/to-cart-item';
import useTest8629 from 'hooks/test/use-test-8629';
import { SavingsPlan } from 'interfaces/customer';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSavingPlanStore } from 'stores/saving-plan';
import { useRootStore } from 'stores/use-root-store';
import useSWR from 'swr';
import { ProductType } from 'interfaces/product';
import { useCartSavingsPlanCleanup } from './use-cart-savings-plan-cleanup';
import {
  findCartItemByVariantId,
  findUpdatedPlan,
  getSavingsPlanLoadingState,
  hasVariantChanged,
  removeFromLocalStorage,
  transformPetsForSavingsPlan,
} from './utils';

interface SavingsPlanHookReturn {
  cleverPlan: Plan | undefined;
  error: Error | null;
  isLoading: boolean;
  isPlanInCart: boolean;
  premiumPlan: Plan | undefined;
  getPlanByType: (type: string) => Plan | undefined;
  getPlanToAddToCart: (plan?: Plan | null) => Plan | null;
  isPlanActive: (plan: Plan | SavingsPlan | null) => boolean;
  isPlanInCartByPlan: (plan: Plan | SavingsPlan | null) => boolean;
}

export const SavingsPlanDefaultValues = {
  Clever: {
    price: 2.99,
    discountPercentage: 3,
  },
  Premium: {
    price: 7.99,
    discountPercentage: 5,
  },
} as const;

/**
 * Hook to fetch and manage savings plans
 */
export const useSavingsPlan = (): SavingsPlanHookReturn => {
  const isUpdatingPlanRef = useRef(false);
  const { reload } = useRouter();
  const { items, updateCart, addToCart } = useCart();
  const { customer, loading: authLoading, isUserAuthenticated } = useAuth();
  const currentPets = useRootStore((store) => store.customerPets);

  const { featureFlags } = useConfig();
  const isSavingsPlanEnabled = featureFlags.savingsPlan;

  const { showSavingsPlanTest } = useTest8629();

  // Get saving plan store
  const activePlan = useSavingPlanStore((state) => state.activePlan);
  const availablePlans = useSavingPlanStore((state) => state.availablePlans);
  const isPlanFromCustomer = useSavingPlanStore((state) => state.isPlanFromCustomer);
  const hasHydrated = useSavingPlanStore((state) => state._hasHydrated);
  const setActivePlan = useSavingPlanStore((state) => state.setActivePlan);
  const setCustomerPlan = useSavingPlanStore((state) => state.setCustomerPlan);
  const setAvailablePlans = useSavingPlanStore((state) => state.setAvailablePlans);
  const clearActivePlan = useSavingPlanStore((state) => state.clearActivePlan);

  // Clean up stale savings plan when the Contentful test is off
  // and the user doesn't have a purchased plan
  useEffect(() => {
    if (!showSavingsPlanTest && !isPlanFromCustomer) {
      // Remove all savings_plan product type items from the cart
      const plansInCart = items?.filter((item) => item.productType === ProductType.SavingsPlan);

      if (plansInCart?.length) {
        updateCart(
          plansInCart.map((item) => ({
            target: toCartItem({ item, shouldQuantity: false }),
            updateData: { variantId: String(item.variantId), quantity: 0 },
          }))
        );
      }

      // Clean up localStorage cart for all known plan variant IDs
      const savingsPlanVariantIds = availablePlans.map((plan) => String(plan.variantId));

      if (isPlanFromAPI(activePlan) && activePlan.variantId) {
        savingsPlanVariantIds.push(String(activePlan.variantId));
      }

      savingsPlanVariantIds.forEach((variantId) => {
        removeFromLocalStorage({ localCartKey: LOCAL_CART_KEY, oldVariantId: variantId });
      });

      if (activePlan) {
        clearActivePlan();
      }
    }
  }, [
    showSavingsPlanTest,
    activePlan,
    isPlanFromCustomer,
    clearActivePlan,
    items,
    updateCart,
    availablePlans,
  ]);

  // Transform current pets to match backend format
  const transformedPets = useMemo(
    () => transformPetsForSavingsPlan(currentPets.length > 0 ? currentPets : undefined),
    [currentPets]
  );

  // Set customer's active plan if it exists
  useEffect(() => {
    if (customer?.savingsPlan) {
      setCustomerPlan(customer.savingsPlan);
    } else if (!customer && isPlanFromCustomer) {
      // User logged out, clear customer plan
      setCustomerPlan(null);
    }
  }, [customer?.savingsPlan, customer, isPlanFromCustomer, setCustomerPlan]);

  // Generate SWR cache key - skip fetch if customer has an active plan or feature is disabled
  const getSwrKey = (): Array<unknown> | null => {
    if (!isSavingsPlanEnabled) return null;

    // If customer has an active plan, don't fetch available plans
    if (customer?.savingsPlan) return null;

    const petIds = currentPets.map((pet) => pet.id);

    // For registered customers without active plan
    if (customer?.id && isUserAuthenticated) {
      return ['savings-plans', customer.id, isUserAuthenticated, JSON.stringify(petIds)];
    }

    // Always make the call, even without pet profiles to get default plans
    return ['savings-plans-local', JSON.stringify(petIds)];
  };

  const { isLoading: swrIsLoading, error } = useSWR(
    getSwrKey(),
    () => {
      const payload: Parameters<typeof getSavingsPlans>[0] = {};

      if (customer?.id && isUserAuthenticated) payload.customer_id = customer.id.toString();

      if (transformedPets) payload.pets = transformedPets;

      return getSavingsPlans(payload);
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 2000,
      onSuccess: async (data) => {
        setAvailablePlans(data);

        // Handle cart cleanup when activePlan variant changes
        // Only for plans from API (not customer plans)
        if (!isPlanFromAPI(activePlan)) return;

        const updatedPlan = findUpdatedPlan(data, activePlan.planName);

        if (!updatedPlan) {
          return;
        }

        const oldVariantId = String(activePlan.variantId);
        const newVariantId = String(updatedPlan.variantId);

        // Check if variant ID has changed
        if (!hasVariantChanged(oldVariantId, newVariantId)) {
          // No variant change, just update the active plan
          setActivePlan(updatedPlan);
          return;
        }

        // Variant changed - need to handle cart cleanup
        if (isUpdatingPlanRef.current) {
          return;
        }

        // Handle case when cart items are not available (user not logged in or cart not loaded)
        if (!items) {
          setActivePlan(null);

          // Try to remove from localStorage and reload
          const removed = removeFromLocalStorage({
            localCartKey: LOCAL_CART_KEY,
            oldVariantId,
          });

          if (removed) {
            reload();
          }
          return;
        }

        // Handle case when cart items are available
        const oldPlanInCart = findCartItemByVariantId(items, oldVariantId);

        if (oldPlanInCart) {
          // Old plan is in cart - replace it with updated plan
          isUpdatingPlanRef.current = true;

          try {
            updateCart([
              {
                target: toCartItem({ item: oldPlanInCart, shouldQuantity: false }),
                updateData: {
                  variantId: String(oldVariantId),
                  quantity: 0,
                },
              },
            ]);

            addToCart([
              {
                variantId: String(updatedPlan.variantId),
                quantity: 1,
              },
            ]);
          } catch (error) {
            console.error('[useSavingsPlan] Error updating plan:', error);
          } finally {
            isUpdatingPlanRef.current = false;
          }
        }

        // Always update the active plan with the latest data
        setActivePlan(updatedPlan);
      },
    }
  );

  // Calculate loading state
  const customerHasPlan = !!customer?.savingsPlan;
  const { isLoading } = getSavingsPlanLoadingState({
    activePlan,
    authLoading,
    customerHasPlan,
    isPlanFromCustomer,
    swrIsLoading,
    customerSavingsPlan: customer?.savingsPlan,
  });

  // Clean up cart when customer with existing plan logs in
  useCartSavingsPlanCleanup({ availablePlans, hasHydrated });

  // Find specific plans
  const cleverPlan = availablePlans.find((p) => p.planName === SavingPlanTypes.Clever);
  const premiumPlan = availablePlans.find((p) => p.planName === SavingPlanTypes.Premium);

  // Helper to check if a plan is active
  const isPlanActive = useCallback(
    (plan: Plan | SavingsPlan | null): boolean => activePlan?.planName === plan?.planName,
    [activePlan]
  );

  // Helper to get plan by type
  const getPlanByType = useCallback(
    (type: string): Plan | undefined => availablePlans.find((p) => p.planName === type),
    [availablePlans]
  );

  // Helper to check if a specific plan is already in the cart
  const isPlanInCartByPlan = useCallback(
    (plan: Plan | SavingsPlan | null): boolean => {
      // Customer plans are not managed via cart
      if (isPlanFromCustomer) return false;

      if (!isPlanFromAPI(plan) || !plan.variantId) return false;

      return items?.some((item) => String(item.variantId) === String(plan.variantId));
    },
    [items, isPlanFromCustomer]
  );

  // Check if the active plan is in the cart
  const isPlanInCart = useMemo(
    () => isPlanInCartByPlan(activePlan),
    [activePlan, isPlanInCartByPlan]
  );

  // Helper to get the plan that should be added to cart (returns null if shouldn't add)
  const getPlanToAddToCart = useCallback(
    (plan?: Plan | null): Plan | null => {
      // Customer plans are not added to cart
      if (isPlanFromCustomer) return null;

      const planToCheck = plan || activePlan;

      if (
        planToCheck &&
        isPlanFromAPI(planToCheck) &&
        planToCheck.variantId &&
        !isPlanInCartByPlan(planToCheck)
      ) {
        return planToCheck as Plan;
      }

      return null;
    },
    [activePlan, isPlanInCartByPlan, isPlanFromCustomer]
  );

  return {
    cleverPlan,
    error,
    isLoading,
    isPlanInCart,
    premiumPlan,
    getPlanByType,
    getPlanToAddToCart,
    isPlanActive,
    isPlanInCartByPlan,
  };
};
